/**
 * Declarative layout for the raw Profit & Loss Statement table.
 *
 * Row order is explicit — never inferred from JSON key insertion order.
 * Lookup keys match stored profitAndLoss object labels exactly.
 */

export const PROFIT_LOSS_STATEMENT_SECTIONS = [
    {
        id: 'sales',
        totalRowLabel: 'Sales',
        driverRowLabels: ['Sales Growth %'],
    },
    {
        id: 'expenses',
        totalRowLabel: 'Expenses',
        driverRowLabels: [
            'Material Cost %',
            'Manufacturing Cost %',
            'Employee Cost %',
            'Other Cost %',
        ],
    },
    {
        id: 'operatingProfit',
        totalRowLabel: 'Operating Profit',
        driverRowLabels: ['OPM %'],
        collapsible: false,
        totalRole: 'subtotal',
    },
    {
        id: 'otherIncome',
        totalRowLabel: 'Other Income',
        driverRowLabels: [
            'Exceptional items',
            'Other income normal',
        ],
    },
    {
        id: 'interest',
        totalRowLabel: 'Interest',
        collapsible: false,
        totalRole: 'plain',
    },
    {
        id: 'depreciation',
        totalRowLabel: 'Depreciation',
        collapsible: false,
        totalRole: 'plain',
    },
    {
        id: 'profitBeforeTax',
        totalRowLabel: 'Profit before tax',
        driverRowLabels: ['Tax %'],
        collapsible: false,
        totalRole: 'subtotal',
    },
    {
        id: 'netProfit',
        totalRowLabel: 'Net Profit',
        driverRowLabels: [],
    },
    {
        id: 'eps',
        totalRowLabel: 'EPS in Rs',
        collapsible: false,
        totalRole: 'plain',
    },
    {
        id: 'dividend',
        totalRowLabel: 'Dividend Payout %',
        collapsible: false,
        totalRole: 'plain',
    },
];

/** Leftover Screener box labels kept as a skip set — not rendered as statement rows. */
export const PROFIT_LOSS_SUPPLEMENTARY_ROW_LABELS = [
    'Compounded Sales Growth',
    '10 Years:',
    '5 Years:',
    '3 Years:',
    'TTM:',
    'Compounded Profit Growth',
    'Stock Price CAGR',
    '1 Year:',
    'Return on Equity',
    'Last Year:',
];

export const PROFIT_LOSS_OTHER_ITEMS_LABEL = 'Other items';
export const PROFIT_LOSS_UNCLASSIFIED_LABEL = 'Unclassified';
export const PROFIT_LOSS_DERIVED_ANALYSIS_LABEL = 'Growth & Return Summary';

const COLLAPSIBLE_SECTION_IDS = new Set(
    PROFIT_LOSS_STATEMENT_SECTIONS
        .filter((section) => section.collapsible !== false && (section.totalRole || 'total') === 'total')
        .map((section) => section.id),
);

function collectKnownLabels() {
    const known = new Set();
    PROFIT_LOSS_STATEMENT_SECTIONS.forEach((section) => {
        if (section.totalRowLabel) known.add(section.totalRowLabel);
        (section.driverRowLabels || []).forEach((label) => known.add(label));
        if (section.subtotalRowLabel) known.add(section.subtotalRowLabel);
        (section.trailingDriverRowLabels || []).forEach((label) => known.add(label));
        (section.scheduleSubtotalRowLabels || []).forEach((label) => known.add(label));
    });
    PROFIT_LOSS_SUPPLEMENTARY_ROW_LABELS.forEach((label) => known.add(label));
    return known;
}

export const PROFIT_LOSS_KNOWN_ROW_LABELS = collectKnownLabels();

/**
 * Best-effort parent section for an unexpected line item.
 * Ambiguous labels stay unclassified rather than being forced into a section.
 *
 * @param {string} label
 * @returns {'sales' | 'expenses' | 'otherIncome' | 'netProfit' | 'unclassified'}
 */
export function inferUnmappedSection(label) {
    const text = String(label || '').toLowerCase();
    if (!text.trim()) return 'unclassified';

    if (/minority|profit excl|profit for pe|profit for eps|profit growth|exceptional items at/.test(text)) {
        return 'netProfit';
    }
    if (/other income|exceptional/.test(text)) return 'otherIncome';
    if (/sales|revenue|turnover/.test(text)) return 'sales';
    if (/material|manufactur|employee|expense|cost/.test(text)) return 'expenses';

    return 'unclassified';
}

function hasRow(data, label) {
    return Boolean(data) && Object.prototype.hasOwnProperty.call(data, label);
}

function pushDataRow(rows, data, label, role, sectionId) {
    if (!hasRow(data, label)) return;
    rows.push({ kind: 'data', label, role, sectionId: sectionId || null });
}

function resolveTotalRole(section) {
    const role = section.totalRole || 'total';
    if (role === 'subtotal' && section.collapsible === false) return 'statementSubtotal';
    return role;
}

/**
 * Flatten profit-and-loss JSON into presentation rows.
 * Missing mapped labels are omitted. Unmapped labels are never dropped.
 *
 * @param {Record<string, unknown>|null|undefined} profitAndLoss
 * @returns {Array<{
 *   kind: 'data' | 'groupLabel' | 'divider',
 *   label: string,
 *   role: 'total' | 'driver' | 'subtotal' | 'statementSubtotal' | 'plain' | 'other' | 'grandTotal' | 'supplementary' | 'groupLabel' | 'divider',
 *   sectionId: string|null,
 * }>}
 */
export function buildProfitLossStatementRows(profitAndLoss) {
    const data = profitAndLoss && typeof profitAndLoss === 'object' ? profitAndLoss : {};
    const unmappedBySection = {
        sales: [],
        expenses: [],
        otherIncome: [],
        netProfit: [],
        unclassified: [],
    };

    Object.keys(data).forEach((label) => {
        if (PROFIT_LOSS_KNOWN_ROW_LABELS.has(label)) return;
        const sectionId = inferUnmappedSection(label);
        unmappedBySection[sectionId].push(label);
    });

    const rows = [];

    PROFIT_LOSS_STATEMENT_SECTIONS.forEach((section) => {
        pushDataRow(rows, data, section.totalRowLabel, resolveTotalRole(section), section.id);
        (section.driverRowLabels || []).forEach((label) => {
            pushDataRow(rows, data, label, 'driver', section.id);
        });
        if (section.subtotalRowLabel) {
            pushDataRow(rows, data, section.subtotalRowLabel, 'subtotal', section.id);
        }
        (section.trailingDriverRowLabels || []).forEach((label) => {
            pushDataRow(rows, data, label, 'driver', section.id);
        });
        (section.scheduleSubtotalRowLabels || []).forEach((label) => {
            pushDataRow(rows, data, label, 'subtotal', section.id);
        });

        const extras = unmappedBySection[section.id];
        if (extras && extras.length) {
            rows.push({
                kind: 'groupLabel',
                label: PROFIT_LOSS_OTHER_ITEMS_LABEL,
                role: 'groupLabel',
                sectionId: section.id,
            });
            extras.forEach((label) => {
                rows.push({ kind: 'data', label, role: 'other', sectionId: section.id });
            });
        }
    });

    if (unmappedBySection.unclassified.length) {
        rows.push({
            kind: 'groupLabel',
            label: PROFIT_LOSS_UNCLASSIFIED_LABEL,
            role: 'groupLabel',
            sectionId: null,
        });
        unmappedBySection.unclassified.forEach((label) => {
            rows.push({ kind: 'data', label, role: 'other', sectionId: null });
        });
    }

    return rows;
}

export function isProfitLossSectionCollapsible(sectionId) {
    return COLLAPSIBLE_SECTION_IDS.has(sectionId);
}

/**
 * Whether a layout row should render given the section expand map.
 * Section totals stay visible when collapsed; their nested rows hide.
 *
 * @param {{ role: string, sectionId: string|null }} row
 * @param {Record<string, boolean>} expanded
 */
export function isProfitLossStatementRowVisible(row, expanded) {
    if (!row) return false;
    if (
        row.role === 'total'
        || row.role === 'grandTotal'
        || row.role === 'supplementary'
        || row.role === 'divider'
        || row.role === 'plain'
        || row.role === 'statementSubtotal'
    ) {
        return true;
    }
    if (row.sectionId && isProfitLossSectionCollapsible(row.sectionId)) {
        return expanded?.[row.sectionId] !== false;
    }
    return true;
}
